import { useAuthStore } from '@stores/auth';

export default ({ app }) => {
    const authStore = useAuthStore();
    const ACTIONS = [
        'view',
        'create',
        'edit',
        'delete',
        'merge',
        'split',
        'always_dismiss'
    ];
    const PERMISSIONS = {
        viewer: {
            assets: ['view'],
            categories: ['view'],
            causes: ['view'],
            facilities: ['view'],
            groups: ['view'],
            mfa_methods: [],
            phases: ['view'],
            products: ['view'],
            recipients: ['view'],
            reports: ['view'],
            shifts: ['view'],
            downtime_notifications: ['always_dismiss'],
            users: ['view'],
            quality_defects: ['view'],
            quality_notifications: ['always_dismiss'],
            event_alert_notifications: [],
            quality_events: []
        },
        operator: {
            assets: ['view'],
            categories: ['create', 'delete', 'edit', 'view'],
            causes: ['create', 'delete', 'edit', 'view'],
            facilities: ['view'],
            groups: ['create', 'delete', 'edit', 'view'],
            mfa_methods: [],
            phases: ['create', 'edit', 'merge', 'split', 'view'],
            products: ['create', 'delete', 'edit', 'view'],
            recipients: ['create', 'delete', 'edit', 'view'],
            reports: [],
            shifts: ['view'],
            downtime_notifications: [],
            users: ['view'],
            quality_defects: ['view'],
            quality_notifications: [],
            quality_events: ['edit'],
            event_alert_notifications: ['always_dismiss']
        },
        manager: {
            assets: ['edit', 'view'],
            categories: ['create', 'delete', 'edit', 'view'],
            causes: ['create', 'delete', 'edit', 'view'],
            facilities: ['edit', 'view'],
            groups: ['create', 'delete', 'edit', 'view'],
            mfa_methods: ['create', 'delete', 'edit', 'view'],
            phases: ['create', 'edit', 'merge', 'split', 'view'],
            products: ['create', 'delete', 'edit', 'view'],
            recipients: ['create', 'delete', 'edit', 'view'],
            reports: ['view'],
            shifts: ['create', 'delete', 'edit', 'view'],
            downtime_notifications: ['always_dismiss'],
            users: ['create', 'delete', 'edit', 'view'],
            quality_defects: ['create', 'delete', 'edit', 'view'],
            quality_notifications: ['always_dismiss'],
            quality_events: ['edit'],
            event_alert_notifications: ['always_dismiss']
        },
        admin: {
            assets: ['view', 'edit'],
            categories: ['create', 'delete', 'edit', 'view'],
            causes: ['create', 'delete', 'edit', 'view'],
            facilities: ['edit', 'view'],
            groups: ['create', 'delete', 'edit', 'view'],
            mfa_methods: ['create', 'delete', 'edit', 'view'],
            phases: ['create', 'edit', 'merge', 'split', 'view'],
            products: ['create', 'delete', 'edit', 'view'],
            recipients: ['create', 'delete', 'edit', 'view'],
            reports: ['view'],
            shifts: ['create', 'delete', 'edit', 'view'],
            downtime_notifications: ['always_dismiss'],
            users: ['create', 'delete', 'edit', 'view'],
            quality_defects: ['create', 'delete', 'edit', 'view'],
            quality_notifications: ['always_dismiss'],
            quality_events: ['edit'],
            event_alert_notifications: ['always_dismiss']
        }
    };

    /**
     * Determine if the user can perform an action on a resource.
     * @param {String} resource
     * @param {String} action
     */
    function userCan(action, resource) {
        const userRole = authStore.getUserRole;

        if (!ACTIONS.includes(action)) {
            throw new Error(`action ${action} is not defined in ACTIONS`);
        }

        // On logout, userRole is null so we need to check for that first
        if (!userRole) {
            // user is not logged in
            return false;
        } else if ((!userRole) in PERMISSIONS) {
            console.error(`userRole ${userRole} not defined in PERMISSIONS`);
            return false;
        } else if (!(resource in PERMISSIONS[userRole])) {
            console.error(
                `resource ${resource} not defined for user role ${userRole}`
            );
            return false;
        } else if (!PERMISSIONS[userRole][resource].includes(action)) {
            return false;
        }

        return true;
    }

    /**
     * Negation of userCan for readability.
     * @param {String} resource
     * @param {String} action
     * @return {Boolean}
     */
    function userCannot(action, resource) {
        return !userCan(action, resource);
    }

    /**
     * User is a viewer role
     * @return {Boolean}
     */
    function isUserViewer() {
        const userRole = authStore.getUserRole;
        return userRole === 'viewer';
    }

    /**
     * User is an operator role
     * @return {Boolean}
     */

    function isUserOperator() {
        const userRole = authStore.getUserRole;
        return userRole === 'operator';
    }

    /**
     * User is a manager role
     *  @return {Boolean}
     */
    function isUserManager() {
        const userRole = authStore.getUserRole;
        return userRole === 'manager';
    }

    /**
     * User is an admin role
     * @return {Boolean}
     */
    function isUserAdmin() {
        const userRole = authStore.getUserRole;
        return userRole === 'admin';
    }

    // expose method to be used inside Vue components & templates
    app.config.globalProperties.$userCan = userCan;
    app.config.globalProperties.$userCannot = userCannot;
    app.config.globalProperties.$isUserViewer = isUserViewer;
    app.config.globalProperties.$isUserOperator = isUserOperator;
    app.config.globalProperties.$isUserManager = isUserManager;
    app.config.globalProperties.$isUserAdmin = isUserAdmin;
};
